import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ArrowBack, Save, ExpandMore, Person } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const EnterMarks = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [marks, setMarks] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [loadingMarks, setLoadingMarks] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch class data
        const classResponse = await teacherAPI.getClasses();
        const classes = classResponse.data.classes || [];
        const currentClass = classes.find(c => c.id === id);
        setClassData(currentClass);

        // Fetch students
        const studentsResponse = await teacherAPI.getStudents();
        const allStudents = studentsResponse.data.students || [];
        const classStudents = allStudents.filter(s => s.class_id === id);
        setStudents(classStudents);

        // Fetch subjects for this class
        const subjectsResponse = await teacherAPI.getClassSubjects(id);
        setSubjects(subjectsResponse.data.subjects || []);

        // Fetch active exams for this class
        const examsResponse = await teacherAPI.getClassExams(id);
        setExams(examsResponse.data.exams || []);

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // When exam selection changes, load existing marks for that exam
  useEffect(() => {
    if (!selectedExam || !id) return;

    const fetchExistingMarks = async () => {
      try {
        setLoadingMarks(true);
        const response = await teacherAPI.getClassMarks(id, selectedExam);
        const existingMarks = response.data.marks || [];

        // Populate marks state with existing data
        const marksMap = {};
        existingMarks.forEach(m => {
          marksMap[`${m.student_id}-${m.subject_id}`] = String(m.marks);
        });
        setMarks(marksMap);
      } catch (error) {
        console.error('Error fetching existing marks:', error);
      } finally {
        setLoadingMarks(false);
      }
    };

    fetchExistingMarks();
  }, [selectedExam, id]);

  const handleExamChange = (e) => {
    setSelectedExam(e.target.value);
    setMarks({});
    setError('');
    setSuccess('');
  };

  const handleAccordionChange = (studentId) => (event, isExpanded) => {
    setExpandedStudent(isExpanded ? studentId : null);
  };

  const handleMarkChange = (studentId, subjectId, value) => {
    // Allow empty or valid numbers 0-100
    if (value !== '' && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
      return;
    }
    setMarks(prev => ({
      ...prev,
      [`${studentId}-${subjectId}`]: value
    }));
    setError('');
  };

  const handleSaveMarks = async (studentId) => {
    if (!selectedExam) {
      setError('Please select an exam first');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const studentMarks = subjects.map(subject => ({
        student_id: studentId,
        subject_id: subject.id,
        marks: marks[`${studentId}-${subject.id}`] || ''
      })).filter(m => m.marks !== '');

      if (studentMarks.length === 0) {
        setError('Please enter at least one mark');
        setSaving(false);
        return;
      }

      await teacherAPI.saveMarks({
        marks: studentMarks,
        exam_id: selectedExam,
        class_id: id
      });

      const studentName = students.find(s => s.id === studentId)?.name;
      setSuccess(`Marks saved successfully for ${studentName}`);
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      console.error('Save marks error:', error.response?.data || error);
      const msg = error.response?.data?.message || error.message || 'Failed to save marks';
      const details = error.response?.data?.errors ? ` | Detail: ${JSON.stringify(error.response.data.errors[0])}` : '';
      setError(msg + details);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllMarks = async () => {
    if (!selectedExam) {
      setError('Please select an exam first');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const allMarks = [];
      students.forEach(student => {
        subjects.forEach(subject => {
          const mark = marks[`${student.id}-${subject.id}`];
          if (mark !== undefined && mark !== '') {
            allMarks.push({
              student_id: student.id,
              subject_id: subject.id,
              marks: mark
            });
          }
        });
      });

      if (allMarks.length === 0) {
        setError('Please enter at least one mark');
        setSaving(false);
        return;
      }

      await teacherAPI.saveMarks({
        marks: allMarks,
        exam_id: selectedExam,
        class_id: id
      });

      setSuccess(`All marks saved successfully! (${allMarks.length} entries)`);

    } catch (error) {
      console.error('Save all marks error:', error.response?.data || error);
      const msg = error.response?.data?.message || error.message || 'Failed to save marks';
      const details = error.response?.data?.errors ? ` | Detail: ${JSON.stringify(error.response.data.errors[0])}` : '';
      setError(msg + details);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Enter Marks
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {classData?.name} {classData?.grade && `• Grade ${classData.grade}`} {classData?.section && `• Section ${classData.section}`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/teacher/classes/${id}/manage`)}
        >
          Back
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Exam Selection Dropdown */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="exam-select-label">Select Exam</InputLabel>
          <Select
            labelId="exam-select-label"
            value={selectedExam}
            label="Select Exam"
            onChange={handleExamChange}
          >
            {exams.length === 0 ? (
              <MenuItem disabled value="">
                No active exams available
              </MenuItem>
            ) : (
              exams.map((exam) => (
                <MenuItem key={exam.id} value={exam.id}>
                  {exam.name} ({exam.academic_year})
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        {exams.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No active exams found. Please ask the Principal to create an exam first.
          </Typography>
        )}
      </Paper>

      {!selectedExam ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="h6">
            Please select an exam to enter marks
          </Typography>
        </Paper>
      ) : loadingMarks ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading existing marks...</Typography>
        </Box>
      ) : students.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="h6">
            No students in this class
          </Typography>
        </Paper>
      ) : subjects.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="h6">
            No subjects added yet
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Please add subjects before entering marks
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate(`/teacher/classes/${id}/subjects`)}
          >
            Add Subjects
          </Button>
        </Paper>
      ) : (
        <>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={2}>
            <Typography variant="body1" color="text.secondary">
              Total Students: {students.length} | Exam: {exams.find(e => e.id === selectedExam)?.name}
            </Typography>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSaveAllMarks}
              disabled={saving}
            >
              Save All Marks
            </Button>
          </Box>

          {students.map((student) => (
            <Accordion
              key={student.id}
              expanded={expandedStudent === student.id}
              onChange={handleAccordionChange(student.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`student-${student.id}-content`}
                id={`student-${student.id}-header`}
              >
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Person color="primary" />
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="bold">
                      {student.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Admission No: {student.admission_number}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${subjects.filter(s => marks[`${student.id}-${s.id}`]).length}/${subjects.length} subjects`}
                    size="small"
                    color={subjects.filter(s => marks[`${student.id}-${s.id}`]).length === subjects.length ? 'success' : 'default'}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Enter Marks for Each Subject
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    {subjects.map((subject) => (
                      <Grid item xs={12} sm={6} md={4} key={subject.id}>
                        <TextField
                          fullWidth
                          label={subject.subject_name}
                          helperText={`Code: ${subject.subject_code}`}
                          type="number"
                          inputProps={{ min: 0, max: 100 }}
                          value={marks[`${student.id}-${subject.id}`] || ''}
                          onChange={(e) => handleMarkChange(student.id, subject.id, e.target.value)}
                          placeholder="Enter marks (0-100)"
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                      onClick={() => handleSaveMarks(student.id)}
                      disabled={saving}
                    >
                      Save Marks for {student.name}
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </Container>
  );
};

export default EnterMarks;
